import type { InvoiceBulkTarget } from '@clockwise/clients/invoices';
import { DatePicker } from '@clockwise/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { invoicesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { todayIso } from '@/lib/user-display';
import {
  invoiceDatePreviewSummary,
  runBulkInChunks,
  summarizeFailures,
} from './invoice-bulk-selection';

/**
 * Set the invoice date of the selected drafts — the bulk form of the editor's
 * Invoice date field.
 *
 * Before anything is written, the chosen date is run through the server as a
 * DRY RUN over the same target, and the dialog states what will actually
 * happen: how many invoices change, and which will be skipped and why. The
 * confirm button quotes the dry run's count, not the selection's, so nobody
 * confirms "12" and gets 9 with the difference only in a toast.
 *
 * `count` is the DRAFTS in the selection: the date is content, so approved
 * invoices are locked and never re-dated from here.
 */
export function InvoiceDateDialog({
  open,
  count,
  target,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  count: number;
  target: InvoiceBulkTarget;
  busy: boolean;
  onConfirm: (invoiceDate: string) => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState('');

  // Fresh each time it opens: last batch's date is not a sensible default.
  useEffect(() => {
    if (open) setDate(todayIso());
  }, [open]);

  const preview = useQuery({
    queryKey: queryKeys.invoices.invoiceDatePreview(target, date),
    queryFn: () =>
      runBulkInChunks(target, (t) => invoicesApi.setInvoiceDate(t, date, { dryRun: true })),
    // Not while the real write is in flight: its `invoices.all` invalidation
    // would otherwise re-run the dry run, which locks the rows it judges.
    enabled: open && date !== '' && count > 0 && !busy,
    placeholderData: keepPreviousData,
    // A dry run describes the rows as they are NOW; never serve a stale one.
    staleTime: 0,
    gcTime: 0,
    // Nor re-run it on every tab switch — each run takes row locks.
    refetchOnWindowFocus: false,
  });
  // Only an answer for THIS date counts — a placeholder from the previous
  // date must not arm the confirm button with the wrong number.
  const settled = preview.isSuccess && !preview.isPlaceholderData;
  const summary = settled ? invoiceDatePreviewSummary(preview.data.data) : null;
  const willChange = summary?.willChange ?? 0;
  const noun = (n: number) => (n === 1 ? 'invoice' : 'invoices');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Change invoice date for {count.toLocaleString()} draft {noun(count)}
          </DialogTitle>
          <DialogDescription>
            Approved invoices are locked, so only drafts are changed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="bulk-invoice-date">Invoice date</Label>
          <DatePicker
            id="bulk-invoice-date"
            value={date}
            onChange={setDate}
            disabled={busy}
            placeholder="Pick a date"
            className="w-40"
          />
          <p className="text-muted-foreground text-xs">
            Each due date moves with it, keeping its payment terms.
          </p>
        </div>

        {date === '' ? null : preview.isError ? (
          <p role="alert" className="text-destructive text-sm">
            Couldn’t check these invoices, so nothing can be changed yet. Try again.
          </p>
        ) : !settled ? (
          <p className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Checking the selected invoices…
          </p>
        ) : summary && (summary.sendConflicts > 0 || summary.otherSkips.length > 0) ? (
          <div
            role="alert"
            data-testid="invoice-date-skip-warning"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
          >
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400"
              aria-hidden
            />
            <div className="flex flex-col gap-1">
              {summary.sendConflicts > 0 ? (
                <span>
                  {summary.sendConflicts.toLocaleString()} {noun(summary.sendConflicts)}{' '}
                  {summary.sendConflicts === 1 ? 'is' : 'are'} scheduled to send before this date
                  and won’t change —{' '}
                  {summary.sendConflicts === 1 ? 'it keeps its' : 'they keep their'} current date
                  and still send as scheduled. Change the send date first to include{' '}
                  {summary.sendConflicts === 1 ? 'it' : 'them'}.
                </span>
              ) : null}
              {summary.otherSkips.length > 0 ? (
                <span>
                  {summary.otherSkips.length.toLocaleString()} more won’t change:{' '}
                  {summarizeFailures(summary.otherSkips)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || date === '' || !settled || willChange === 0}
            onClick={() => onConfirm(date)}
          >
            {settled
              ? `Update ${willChange.toLocaleString()} ${noun(willChange)}`
              : `Update ${noun(count)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
